const RSA_ALGORITHM = {
  name: 'RSA-OAEP',
  modulusLength: 2048,
  publicExponent: new Uint8Array([1, 0, 1]),
  hash: 'SHA-256',
};

const AES_ALGORITHM = {
  name: 'AES-CBC',
  length: 256,
};

const IV_BYTE_LENGTH = 16;
const CONVERSATION_KEY_BYTE_LENGTH = 32;

const getSubtleCrypto = () => {
  if (typeof window === 'undefined' || !window.crypto?.subtle) {
    throw new Error('Web Crypto API is unavailable in this environment');
  }
  return window.crypto.subtle;
};

const base64Encode = (bytes) => {
  if (typeof btoa === 'function') {
    let binary = '';
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return btoa(binary);
  }
  return Buffer.from(bytes).toString('base64');
};

const base64Decode = (base64) => {
  if (typeof atob === 'function') {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
  }
  return new Uint8Array(Buffer.from(base64, 'base64'));
};

const bytesToHex = (bytes) =>
  Array.from(bytes)
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('');

const hexToBytes = (hex) => {
  const normalized = `${hex || ''}`.trim();
  if (!normalized || normalized.length % 2 !== 0) {
    throw new Error('Invalid hex string');
  }
  const bytes = new Uint8Array(normalized.length / 2);
  for (let index = 0; index < normalized.length; index += 2) {
    bytes[index / 2] = Number.parseInt(normalized.slice(index, index + 2), 16);
  }
  return bytes;
};

const arrayBufferToPem = (arrayBuffer, label) => {
  const base64 = base64Encode(new Uint8Array(arrayBuffer));
  const chunks = base64.match(/.{1,64}/g) || [];
  return `-----BEGIN ${label}-----\n${chunks.join('\n')}\n-----END ${label}-----`;
};

const pemToArrayBuffer = (pem, label) => {
  const content = `${pem || ''}`
    .replace(`-----BEGIN ${label}-----`, '')
    .replace(`-----END ${label}-----`, '')
    .replace(/\s+/g, '');
  if (!content) {
    throw new Error(`Invalid PEM content for ${label}`);
  }
  return base64Decode(content).buffer;
};

const randomBytes = (length) => {
  if (typeof window === 'undefined' || !window.crypto) {
    throw new Error('Web Crypto API is unavailable in this environment');
  }
  const output = new Uint8Array(length);
  window.crypto.getRandomValues(output);
  return output;
};

const utf8Encode = (value) => new TextEncoder().encode(`${value ?? ''}`);
const utf8Decode = (value) => new TextDecoder().decode(value);

export async function generateDeviceKeyPairPem() {
  const subtle = getSubtleCrypto();
  const keyPair = await subtle.generateKey(RSA_ALGORITHM, true, ['encrypt', 'decrypt']);
  const publicKeyRaw = await subtle.exportKey('spki', keyPair.publicKey);
  const privateKeyRaw = await subtle.exportKey('pkcs8', keyPair.privateKey);
  return {
    publicKeyPem: arrayBufferToPem(publicKeyRaw, 'PUBLIC KEY'),
    privateKeyPem: arrayBufferToPem(privateKeyRaw, 'PRIVATE KEY'),
  };
}

export async function importPublicKeyPem(publicKeyPem) {
  const subtle = getSubtleCrypto();
  return subtle.importKey(
    'spki',
    pemToArrayBuffer(publicKeyPem, 'PUBLIC KEY'),
    RSA_ALGORITHM,
    false,
    ['encrypt'],
  );
}

export async function importPrivateKeyPem(privateKeyPem) {
  const subtle = getSubtleCrypto();
  return subtle.importKey(
    'pkcs8',
    pemToArrayBuffer(privateKeyPem, 'PRIVATE KEY'),
    RSA_ALGORITHM,
    false,
    ['decrypt'],
  );
}

export function generateConversationKeyHex() {
  return bytesToHex(randomBytes(CONVERSATION_KEY_BYTE_LENGTH));
}

export async function encryptConversationKeyForDevice(conversationKeyHex, publicKeyPem) {
  const subtle = getSubtleCrypto();
  const publicKey = await importPublicKeyPem(publicKeyPem);
  const encrypted = await subtle.encrypt(RSA_ALGORITHM, publicKey, utf8Encode(conversationKeyHex));
  return base64Encode(new Uint8Array(encrypted));
}

export async function decryptConversationKeyFromBundle(encryptedKeyBase64, privateKeyPem) {
  const subtle = getSubtleCrypto();
  const privateKey = await importPrivateKeyPem(privateKeyPem);
  const decrypted = await subtle.decrypt(
    RSA_ALGORITHM,
    privateKey,
    base64Decode(encryptedKeyBase64),
  );
  return utf8Decode(new Uint8Array(decrypted));
}

async function importConversationKeyHex(conversationKeyHex, usages) {
  const subtle = getSubtleCrypto();
  const keyBytes = hexToBytes(conversationKeyHex);
  if (keyBytes.length !== CONVERSATION_KEY_BYTE_LENGTH) {
    throw new Error('Conversation key must be 32 bytes (64 hex chars)');
  }

  return subtle.importKey('raw', keyBytes, AES_ALGORITHM, false, usages);
}

export async function encryptTextForConversation(conversationKeyHex, plaintext) {
  const subtle = getSubtleCrypto();
  const iv = randomBytes(IV_BYTE_LENGTH);
  const aesKey = await importConversationKeyHex(conversationKeyHex, ['encrypt']);
  const encrypted = await subtle.encrypt(
    { name: AES_ALGORITHM.name, iv },
    aesKey,
    utf8Encode(plaintext),
  );
  return `${bytesToHex(iv)}:${bytesToHex(new Uint8Array(encrypted))}`;
}

export async function decryptTextForConversation(conversationKeyHex, encryptedPayload) {
  const subtle = getSubtleCrypto();
  const [ivHex, ciphertextHex] = `${encryptedPayload || ''}`.split(':');
  if (!ivHex || !ciphertextHex) {
    throw new Error('Encrypted payload is not in "iv:ciphertext" format');
  }

  const ivBytes = hexToBytes(ivHex);
  const ciphertextBytes = hexToBytes(ciphertextHex);
  if (ivBytes.length !== IV_BYTE_LENGTH) {
    throw new Error('Invalid IV length');
  }

  const aesKey = await importConversationKeyHex(conversationKeyHex, ['decrypt']);
  const decrypted = await subtle.decrypt(
    { name: AES_ALGORITHM.name, iv: ivBytes },
    aesKey,
    ciphertextBytes,
  );
  return utf8Decode(new Uint8Array(decrypted));
}
